import { Box, darken, lighten, styled, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material"
import { CollaboratingGroupContext, FormContext, IFormContext, InstitutionContext, MasterGroupContext, PatientContext } from "@ngt/opms"
import { ConsentStatus, ConsentWorkflow, FormDefinitionType, IEConsentForm } from "@/api/dtos";
import { FC, useContext, useMemo } from "react";
import { useScopedField, } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, IconDefinition, faHourglassHalf } from "@fortawesome/pro-duotone-svg-icons";
import { faHourglassStart } from "@fortawesome/sharp-solid-svg-icons";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import ConsentConfigurationAction from "@/components/consent/ConsentConfigurationAction";
import ConsentNotificationAction from "@/components/consent/ConsentNotificationAction";
import ConsentCompleteAction from "./ConsentCompleteAction";

interface StyledStatusBoxProps {
    consentStatus?: null | ConsentStatus;
}

interface StyledStatusIconProps {
    consentStatus?: null | ConsentStatus;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledStatusButtonsBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}))

const StyledStatusIcon = styled(Box, {
    shouldForwardProp: prop => prop !== 'consentStatus',
})<StyledStatusIconProps>(({ theme, consentStatus }) => {
    const palette = consentStatus === ConsentStatus.Completed
        ? theme.palette.success
        : theme.palette.warning;

    return {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        backgroundColor: lighten(palette.main, 0.4),
        marginTop: '3px'
    };
})

const StyledStatusBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'consentStatus',
})<StyledStatusBoxProps>(({ theme, consentStatus }) => {
    const palette = consentStatus === ConsentStatus.Completed
        ? theme.palette.success
        : theme.palette.warning;

    return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        borderRadiusTop: '4px',
        padding: '1rem',
        background: lighten(palette.main, 0.8),

        '& *': {
            color: darken(palette.main, 0.2),
        },
    };
});

/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */

const consentStatusLabels = {
    [ConsentStatus.InProgress]: 'In Progress',
    [ConsentStatus.AwaitingParticipantSignature]: 'Awaiting Participant Signature',
    [ConsentStatus.AwaitingInvestigatorSignature]: 'Awaiting Investigator Signature',
    [ConsentStatus.Completed]: 'Completed',
} satisfies Record<ConsentStatus, string>;

const consentWorkflowLabels = {
    [ConsentWorkflow.Standard]: 'Standard Consent',
    [ConsentWorkflow.EConsent]: 'eConsent'
} satisfies Record<ConsentWorkflow, string>;

const consentStatusParagraphs = {
    [ConsentStatus.InProgress]: 'Consent form is in progress.',
    [ConsentStatus.AwaitingParticipantSignature]: 'Consent form is awaiting for the participant\'s signature.',
    [ConsentStatus.AwaitingInvestigatorSignature]: 'Consent form is awaiting for the investigator\'s signature.',
    [ConsentStatus.Completed]: 'Consent form is completed.',
} satisfies Record<ConsentStatus, string>;

const consentStatusIcons = {
    [ConsentStatus.InProgress]: faHourglassHalf,
    [ConsentStatus.AwaitingParticipantSignature]: faHourglassHalf,
    [ConsentStatus.AwaitingInvestigatorSignature]: faHourglassHalf,
    [ConsentStatus.Completed]: faCheck,
} satisfies Record<ConsentStatus, IconDefinition>;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

export interface ConsentStatusActionsProps {
    formDefinitionId: FormDefinitionType;
}

const ConsentStatusActions: FC<ConsentStatusActionsProps> = ({
    formDefinitionId,
}) => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: form } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;

    const {
        state: {
            value: workflow
        }
    } = useScopedField<ConsentWorkflow>(`workflow`, { value: true }, false, false);

    const {
        state: {
            value: consentStatus
        }
    } = useScopedField<ConsentStatus>(`consentStatus`, { value: true }, false, false);

    const {
        permissions: [canConsentAdminister],
    } = usePermissionsByIds(
        [
            Permission.EConsentAdminister,
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const consentWorkflowLabel = workflow == null
        ? undefined
        : consentWorkflowLabels[workflow];

    const consentStatusLabel = useMemo(() => {
        const consentStatusStr = consentStatus == null
            ? 'Not Started'
            : consentStatusLabels[consentStatus];

        return consentWorkflowLabel
            ? `${consentWorkflowLabel} - ${consentStatusStr}`
            : consentStatusStr
    }, [consentStatus, consentWorkflowLabel])

    const consentStatusParagraph = consentStatus == null
        ? 'Start to complete the form.'
        : consentStatusParagraphs[consentStatus];

    const consentStatusIcon = consentStatus == null
        ? faHourglassStart
        : consentStatusIcons[consentStatus];

    const investigator = form?.assignedInvestigatorSpecify ||
        form?.assignedInvestigatorUsername ||
        '—';

    const eConsentDetails = useMemo(() => {
        const data: Array<[string, string | undefined]> = [
            ['Participant first name', canConsentAdminister ? form?.participantFirstName : '********'],
            ['Participant last name', canConsentAdminister ? form?.participantLastName : '********'],
            ['Email address', canConsentAdminister ? form?.participantEmailAddress : '********'],
            ['PICF version', form?.picfVersion],
            ['Investigator', investigator],
        ];

        return data;
    }, [canConsentAdminister])

    return (
        <StyledStatusBox
            consentStatus={consentStatus}
        >
            <Box 
                sx={{
                    display: 'flex'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginRight: '0.75rem', 
                        width: '50px' 
                    }}
                >
                    <StyledStatusIcon
                        consentStatus={consentStatus}
                    >
                        <FontAwesomeIcon fontSize="1.75rem" icon={consentStatusIcon} />
                    </StyledStatusIcon>
                </Box>
                <Box>
                    <Typography
                        variant='h6'
                    >
                        <strong>{consentStatusLabel}</strong>
                    </Typography>
                    <Box sx={{ marginTop: '0.25rem' }}>
                        {consentStatusParagraph}

                        {
                            workflow === ConsentWorkflow.EConsent && <>
                                <Table
                                    size="small"
                                    aria-label="eConsent details"
                                    sx={{ mt: 1, width: 'auto' }}
                                >
                                    <TableBody>
                                        {eConsentDetails.map(([label, value]) => (
                                            <TableRow key={label}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                    sx={{ borderBottom: 0, py: 0.25, pl: 0, pr: 3, fontWeight: 600, color: 'inherit' }}
                                                >
                                                    {label}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: 0, py: 0.25, px: 0, color: 'inherit' }}>
                                                    {value || '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        }
                    </Box>
                </Box>
            </Box>

            {
                canConsentAdminister && <>
                    <StyledStatusButtonsBox>
                        {
                            workflow === ConsentWorkflow.EConsent &&
                            consentStatus === ConsentStatus.AwaitingParticipantSignature &&
                            <ConsentNotificationAction
                                recipient="patient"
                                formDefinitionId={formDefinitionId}
                            />
                        }

                        {
                            workflow === ConsentWorkflow.EConsent &&
                            consentStatus === ConsentStatus.AwaitingInvestigatorSignature &&
                            <ConsentNotificationAction
                                recipient="investigator"
                                formDefinitionId={formDefinitionId}
                            />
                        }

                        {
                            !consentStatus && <ConsentConfigurationAction
                                action='start'
                                formDefinitionId={formDefinitionId}
                            />
                        }

                        {
                            !!consentStatus && consentStatus != ConsentStatus.Completed && <ConsentConfigurationAction
                                action='restart'
                                formDefinitionId={formDefinitionId}
                            />
                        }

                        {
                            !!consentStatus && consentStatus != ConsentStatus.Completed && workflow != ConsentWorkflow.EConsent && <ConsentCompleteAction
                                formDefinitionId={formDefinitionId}
                            />
                        }
                    </StyledStatusButtonsBox>
                </>
            }
        </StyledStatusBox>   
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ConsentStatusActions;
